import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '../../../services/dataService';
import { Game } from '../../../data/types';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import StudentLayout from '../../layouts/StudentLayout';
import { AuthContext } from '../../../App';

const GamePlay: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { activeRole } = React.useContext(AuthContext);
  
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [showHint, setShowHint] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  
  // Set student session flag when component mounts
  useEffect(() => {
    // Always set the student session flag to true when accessing this page
    localStorage.setItem('isStudentSession', 'true');
    
    // Only redirect if explicitly authenticated as a non-student role
    if (activeRole && activeRole !== 'Student') {
      navigate('/student/login');
      return;
    }
  }, [navigate, activeRole]);
  
  // Ensure the student session flag is set
  useEffect(() => {
    if (!localStorage.getItem('isStudentSession')) {
      localStorage.setItem('isStudentSession', 'true');
    }
  }, []);
  
  useEffect(() => {
    const fetchGame = async () => {
      try {
        if (!gameId) return;
        
        const allGames = await dataService.getGames();
        const selectedGame = allGames.find(g => g.id === gameId);
        
        if (selectedGame) {
          setGame(selectedGame);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching game data:', error);
        setLoading(false);
      }
    };
    
    fetchGame();
  }, [gameId]);
  
  // Timer effect for the game
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (gameStarted && !gameCompleted && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [gameStarted, gameCompleted, timeLeft]);
  
  // Check if time's up
  useEffect(() => {
    if (timeLeft === 0 && !gameCompleted) {
      handleGameOver();
    }
  }, [timeLeft, gameCompleted]);
  
  const handleStartGame = () => {
    setGameStarted(true);
    setScore(0);
    setLevel(1);
    setLives(3);
    setTimeLeft(60);
    setGameCompleted(false);
  };
  
  const handleAnswer = (correct: boolean) => {
    if (correct) {
      // Increase score
      setScore(prev => prev + (level * 10));
      
      // Level up after every 5 correct answers
      if (score > 0 && score % 50 === 0) {
        setLevel(prev => prev + 1);
      }
      
      // Check if game is completed (arbitrary condition for demo)
      if (score >= 100) {
        handleGameComplete();
      }
    } else {
      // Lose a life
      setLives(prev => {
        const newLives = prev - 1;
        if (newLives <= 0) {
          handleGameOver();
          return 0;
        }
        return newLives;
      });
    }
  };
  
  const handleGameComplete = () => {
    setGameCompleted(true);
    // In a real app, we would save the score to the server here
  };
  
  const handleGameOver = () => {
    setGameCompleted(true);
    // In a real app, we would save the score to the server here
  };
  
  const handleShowHint = () => {
    setShowHint(true);
  };
  
  const handlePlayAgain = () => {
    setGameStarted(false);
    setGameCompleted(false);
  };
  
  const handleBackToGames = () => {
    navigate('/student/games');
  };
  
  if (loading) {
    return (
      <StudentLayout>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-bounce text-4xl text-purple-600">
            Loading your game...
          </div>
        </div>
      </StudentLayout>
    );
  }
  
  if (!game) {
    return (
      <StudentLayout>
        <div className="flex flex-col items-center justify-center h-screen p-4">
          <div className="text-4xl text-red-500 mb-4">Oops!</div>
          <p className="text-xl text-gray-700 mb-6">We couldn't find this game.</p>
          <Button 
            color="purple" 
            onClick={handleBackToGames}
            className="rounded-xl"
          >
            Back to Games
          </Button>
        </div>
      </StudentLayout>
    );
  }
  
  return (
    <StudentLayout>
      <div className="max-w-6xl mx-auto p-4">
        {/* Game Header */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-2xl font-bold text-purple-700">{game.title}</h1>
              <div className="flex items-center mt-1">
                <Badge variant={
                  game.difficulty === 'Easy' ? 'success' : 
                  game.difficulty === 'Medium' ? 'warning' : 'error'
                }>
                  {game.difficulty}
                </Badge>
                <span className="ml-2 text-gray-600">{game.type}</span>
                <span className="ml-2 text-gray-600">Grade {game.gradeLevel}</span>
              </div>
            </div>
            {!gameStarted && !gameCompleted && (
              <Button 
                color="purple" 
                onClick={handleBackToGames}
                className="mt-4 md:mt-0 rounded-lg"
              >
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                  </svg>
                  Back to Games
                </span>
              </Button>
            )}
          </div>
        </div>
        
        {/* Game Container */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {!gameStarted && !gameCompleted ? (
            // Game Start Screen
            <div className="p-8 text-center">
              <div className="mb-6">
                <img 
                  src={game.thumbnail} 
                  alt={game.title} 
                  className="w-full max-w-md mx-auto rounded-lg shadow-md"
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Ready to Play?</h2>
              <p className="text-gray-600 mb-8 max-w-lg mx-auto">
                Test your math skills with {game.title}! This {game.difficulty.toLowerCase()} game will help you practice 
                {game.type === 'Arcade' ? ' in a fun, fast-paced environment' : 
                 game.type === 'Puzzle' ? ' by solving challenging puzzles' : 
                 game.type === 'Quiz' ? ' by answering questions correctly' : 
                 game.type === 'Sorting' ? ' by organizing numbers and shapes' : 
                 game.type === 'DragAndDropSorting' ? ' by dragging items to their correct positions' : 
                 ' with interactive activities'}.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  color="purple" 
                  onClick={handleStartGame}
                  className="text-lg py-3 px-8 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all"
                >
                  <span className="flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Start Game
                  </span>
                </Button>
                <Button 
                  color="gray" 
                  onClick={handleBackToGames}
                  className="rounded-xl"
                >
                  Choose Another Game
                </Button>
              </div>
            </div>
          ) : gameCompleted ? (
            // Game Completed Screen
            <div className="p-8 text-center">
              <div className="mb-6">
                {score >= 100 ? (
                  <div className="w-32 h-32 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                )}
              </div>
              
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {score >= 100 ? 'Great Job!' : 'Game Over!'}
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-lg mx-auto mb-8">
                <div className="bg-purple-100 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-700">{score}</div>
                  <div className="text-sm text-purple-600">Score</div>
                </div>
                <div className="bg-blue-100 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-700">{level}</div>
                  <div className="text-sm text-blue-600">Level</div>
                </div>
                <div className="bg-green-100 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-700">{lives}</div>
                  <div className="text-sm text-green-600">Lives Left</div>
                </div>
                <div className="bg-yellow-100 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-700">{60 - timeLeft}</div>
                  <div className="text-sm text-yellow-600">Seconds</div>
                </div>
              </div>
              
              {score >= 100 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 max-w-lg mx-auto">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-yellow-800">Achievement Unlocked!</h3>
                      <p className="text-sm text-yellow-700">Game Master: Complete {game.title} with a score of 100+</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  color="purple" 
                  onClick={handlePlayAgain}
                  className="rounded-xl"
                >
                  Play Again
                </Button>
                <Button 
                  color="blue" 
                  onClick={handleBackToGames}
                  className="rounded-xl"
                >
                  Back to Games
                </Button>
              </div>
            </div>
          ) : (
            // Active Game Screen
            <div>
              {/* Game HUD */}
              <div className="bg-gray-800 text-white p-4 flex flex-wrap justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div>
                    <div className="text-xs text-gray-400">Score</div>
                    <div className="text-xl font-bold">{score}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Level</div>
                    <div className="text-xl font-bold">{level}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Lives</div>
                    <div className="flex">
                      {[...Array(lives)].map((_, i) => (
                        <svg key={i} xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                      ))}
                      {[...Array(3 - lives)].map((_, i) => (
                        <svg key={i + lives} xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 text-center">Time</div>
                  <div className="text-xl font-bold">{timeLeft}s</div>
                </div>
              </div>
              
              {/* Game Content Area - This would be replaced with the actual game UI */}
              <div className="aspect-video bg-gray-100 flex items-center justify-center p-4">
                {/* This is a placeholder for the actual game content */}
                <div className="text-center max-w-lg">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Game Simulation</h3>
                  <p className="text-gray-600 mb-6">
                    This is a placeholder for the actual game content. In a real implementation, this would be an interactive game based on {game.type}.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <Button 
                      color="green" 
                      onClick={() => handleAnswer(true)}
                      className="py-3 rounded-xl"
                    >
                      Correct Answer
                    </Button>
                    <Button 
                      color="red" 
                      onClick={() => handleAnswer(false)}
                      className="py-3 rounded-xl"
                    >
                      Wrong Answer
                    </Button>
                  </div>
                  
                  {!showHint && (
                    <Button 
                      color="yellow" 
                      onClick={handleShowHint}
                      className="rounded-lg"
                    >
                      <span className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        Show Hint
                      </span>
                    </Button>
                  )}
                  
                  {showHint && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                      <div className="font-bold text-yellow-700 mb-1">Hint:</div>
                      <p className="text-yellow-800">
                        This is a hint that would help the student solve the current problem. The hint would be specific to the current question or challenge in the game.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Game Controls */}
              <div className="p-4 bg-gray-50 flex justify-between">
                <Button 
                  color="gray" 
                  onClick={handleGameOver}
                  className="rounded-lg"
                >
                  End Game
                </Button>
                <Button 
                  color="purple" 
                  onClick={() => handleAnswer(true)}
                  className="rounded-lg"
                >
                  Submit Answer
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default GamePlay;
